import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, watch, nextTick } from '@vue/composition-api';
import { InformationType, PullDown, InformationFile } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';
import vueFilePond from 'vue-filepond';
import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond/dist/filepond.min.css';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import { FileOrigin, FilePondFile } from 'filepond';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                               // Windowサイズ
    isAddMode: boolean;                                                 // true: 登録処理, false: 編集処理
    information: InformationType;                                       // お知らせフォーム
    isAdmin: boolean;
    validateValues: ValidateType;
    bodyErrorMessage: string;                                           // 掲載内容のバリデーションメッセージ文字列
    fileErrorMessage: string;                                           // 添付ファイルのバリデーションメッセージ文字列
    organizationDialog: boolean;                                        // 選択済み公開先ダイアログ表示切替
    organizationTypesPulldown: PullDown[];                              // プルダウン表示データ（組織の種類）
    organizationTypesList: string[];                                    // 選択済み組織の種類一覧
    organizationList: string[];                                         // 選択済み組織一覧
    organizationPulldown: PullDown[];                                   // プルダウン表示データ（組織）
    isFileValid: boolean;                                               // 添付ファイルのバリデーション結果
    isBodyValid: boolean;                                               // 掲載内容のバリデーション結果
    isToolbarBr: boolean;                                               // リッチテキストのツールバーの改行フラグ
    isVisibleStartDateMenu: boolean;                                    // カレンダー表示フラグ（開始日付）
    isVisibleEndDateMenu: boolean;                                      // カレンダー表示フラグ（終了日付）
    isDarkTheme: boolean;                                               // ダークテーマ使用フラグ
    bodyEditorKey: number;                                              // リッチテキストエディターのキー（更新用）
}

// バリデーション用パラメーター
interface ValidateType {
    [key: string]: ValidateValues
}

// バリデーション用パラメーターデータ
type ValidateValues = {
    parameterName: string;                                              // パラメーター名
    value: string;                                                      // パラメーター値
}

// ファイルアップロード
const FilePond = vueFilePond(FilePondPluginGetFile);

/**
 * お知らせ登録画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor, FilePond },
    setup(props, ctx: SetupContext) {
        const form = ref<any>(null);
        const dateRef = ref<any>(null);
        const commonRef = ref<any>(null);                               // メッセージダイアログ
        const pondRef = ref<any>(null);                                 // FilePondへの参照
        const formHeight = SystemConst.FORM_HEIGHT.INFORMATION;         // フォーム高さ調整用変数
        const router = vueRouter;                                       // ルータ
        const severity = {
            // 重要度（選択）の値
            generally: SystemConst.ADD_SEVERITY.GENERALLY,              // 通常
            important: SystemConst.ADD_SEVERITY.IMPORTANT,              // 重要
            administer: SystemConst.ADD_SEVERITY.ADMINISTER             // 管理者
        };
        const createStyleSet = {
            // 作成形式（選択）の値
            simple: SystemConst.CREATE_METHOD.SIMPLE,                   // 簡易登録
            link: SystemConst.CREATE_METHOD.LINK                        // リンクURL登録
        };
        const publishRange = {
            // 公開範囲（選択）の値
            all: SystemConst.INFO_PUBLISH_RANGE.ALL,                    // 全組織の全教職員アカウント（一般を含む）
            selectOrganizationType:
                SystemConst.INFO_PUBLISH_RANGE.
                    SELECT_ORGANIZATION_TYPE,                           // 指定した組織種類の全教職員アカウント（一般を含む）
            selectOrganization:
                SystemConst.INFO_PUBLISH_RANGE.
                    SELECT_ORGANIZATION,                                // 指定した組織の全教職員アカウント（一般を含む）
            forTeacher:
                SystemConst.INFO_PUBLISH_RANGE.FOR_TEACHER,             // 所属組織の全教職員アカウント（一般を含む）
            forTeacherAndStudent:
                SystemConst.INFO_PUBLISH_RANGE.
                    FOR_TEACHER_AND_STUDENT,                            // 所属組織の生徒を含む全アカウント

        };

        // 編集モード時の一時保存用変数
        let backupUrl = '';                                              // URL
        let backupBody = '';                                             // 掲載内容
        let backupBodyText = '';                                         // 掲載内容（テキスト）
        let backupFiles: InformationFile[] = [];                         // 添付ファイル

        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                 // ウィンドウサイズ
            isAddMode: true,                                            // true: 登録処理, false: 編集処理
            information: {
                title: '',
                publishStartDate: '',
                publishStartDateDisplay: '',
                publishEndDate: '',
                publishEndDateDisplay: '',
                severity: severity['generally'],
                organizationType: [],
                organization: [],
                publishRange: publishRange['all'],
                sender: '',
                url: '',
                createMethod: createStyleSet['simple'],
                files: [],
                body: '',
                bodyText: ''
            },
            isAdmin: false,                                             // 管理者ラジオボタンの表示フラグ
            validateValues: {},
            organizationDialog: false,                                  // 選択済み公開先ダイアログ表示切替
            organizationTypesPulldown: [],                              // プルダウン表示データ（組織の種類）
            organizationPulldown: [],                                   // プルダウン表示データ（組織）
            organizationTypesList: [],                                  // 選択済み組織の種類一覧
            organizationList: [],                                       // 選択済み組織一覧
            bodyErrorMessage: '',                                       // 掲載内容のバリデーションメッセージ文字列
            fileErrorMessage: '',                                       // 添付ファイルのバリデーションメッセージ文字列
            isFileValid: true,                                          // 添付ファイルのバリデーション結果
            isBodyValid: true,                                          // 掲載内容のバリデーション結果
            isToolbarBr: false,                                         // リッチテキストのツールバーの改行フラグ
            isVisibleStartDateMenu: false,                              // カレンダー表示フラグ（開始日付）
            isVisibleEndDateMenu: false,                                // カレンダー表示フラグ（終了日付）
            isDarkTheme: ctx.root.$vuetify.theme.dark,                  // ダークテーマ使用フラグ
            bodyEditorKey: 0                                            // リッチテキストエディターのキー（更新用）
        });

        // storeのダークテーマ使用フラグを監視し、ダークテーマ使用フラグの変更を行う
        watch(ctx.root.$store.state.commonLayout, () => {
            state.isDarkTheme = ctx.root.$store.state.commonLayout.isDarkTheme;
        })

        // バリデーション用最大桁数
        const maxLength = {
            // タイトル
            title: SystemConst.INFORMATION_MAX_LENGTH.TITLE,
            // 発信者
            sender: SystemConst.INFORMATION_MAX_LENGTH.SENDER,
            // 掲載内容
            body: SystemConst.INFORMATION_MAX_LENGTH.BODY,
            // URL
            url: SystemConst.INFORMATION_MAX_LENGTH.URL,
        };

        // タイトルのバリデーション
        const titleRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['タイトル']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['title'], 'タイトル')
        ];

        // 掲載開始日、掲載終了日のバリデーション
        const publishDateRules = [
            // 必須チェック
            (v: any) => {
                if (!v) {
                    return CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['掲載日']);
                }
                // 開始と終了が両方入力されているときのみ、掲載期間の逆転チェックを行う
                if (state.information.publishStartDateDisplay &&
                    state.information.publishEndDateDisplay) {
                    const start = Date.parse(state.information.publishStartDateDisplay);
                    const end = Date.parse(state.information.publishEndDateDisplay);
                    if (start > end) {
                        return MessageConst.E_DATE_ORDER;
                    }
                }
            }
        ];

        // 公開範囲のバリデーション
        const publishRangeRules = [
            () => {
                if (state.information.publishRange === publishRange['selectOrganizationType']) {
                    // 公開範囲が「指定した組織種類の全教職員アカウント（一般を含む）」の場合
                    // 公開先（組織の種類）プルダウンの必須チェック
                    if (state.information.organizationType.length === 0) {
                        return CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['組織の種類']);
                    }
                } else if (state.information.publishRange === publishRange['selectOrganization']) {
                    // 公開範囲が「指定した組織の全教職員アカウント（一般を含む）」の場合
                    // 公開先（組織）プルダウンの必須チェック
                    if (state.information.organization.length === 0) {
                        return CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['組織']);
                    }
                }
            }
        ];

        // 発信者のバリデーション
        const senderRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['発信者']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['sender'], '発信者')
        ];

        // 掲載内容のバリデーション
        const bodyRules = [
            () => {
                const files: FilePondFile[] = pondRef.value.getFiles();
                // 必須チェック            
                if (files.length === 0 && state.information.bodyText.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_ONE, ['掲載内容', '添付ファイル']);
                }
                // 桁数チェック
                if (state.information.bodyText.length > maxLength['body']) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_MAX, ['掲載内容', maxLength['body']]);
                }

                return true;
            }
        ];

        // 添付ファイルのバリデーション
        const filesRules = [
            () => {
                // バリデーションはAPIの結果(m_parametersテーブル)を利用するため、
                // APIが呼ばれていない段階では以降を実行しない
                if (!('fileCount' in state.validateValues)) {
                    return;
                }

                // 添付ファイルの一覧をFilePondから得る
                const files: FilePondFile[] = pondRef.value.getFiles();

                // バリデーション値
                const maxCount = Number(state.validateValues['fileCount'].value);
                const maxSize = Number(state.validateValues['fileSize'].value);
                const allowExtension = state.validateValues['fileExtension'].value;

                // ファイル数が上限以下であるか確認する
                if (files.length > maxCount) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_FILE_FILE_COUNT, [
                        "添付",
                        state.validateValues['fileCount'].value + 'ファイル以下']);
                }

                // ファイルサイズが0バイトより大きく、上限以下であるか確認する
                for (const key in files) {
                    if (files[key].fileSize > maxSize) {
                        // 単位をメッセージの単位"KB"に合わせるため、fileSizeを1024で割る
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_MAX_SIZE, [maxSize / 1024]);
                    } else if (files[key].fileSize === 0) {
                        return MessageConst.E_FILE_NO_DATA;
                    }
                }

                // 拡張子が登録対象か確認する
                for (const key in files) {
                    // 登録しようとするファイルの拡張子を得る
                    const parts = files[key].file.name.split(/\.(?=[^.]+$)/);
                    const fileExtension = parts[1];

                    // m_parametersの拡張子一覧
                    const validateExtension = allowExtension.split(',');

                    if (validateExtension.indexOf(fileExtension) === -1) {
                        return CommonFunction.replaceArray(
                            MessageConst.E_FILE_EXTENSION, [allowExtension]);
                    }
                }

                // ファイル名が重複していないか確認する
                if (Array.from(new Set(files.map(e => e.file.name))).length !== files.length) {
                    // 重複を排除した配列の長さが異なれば、重複あり
                    return MessageConst.E_FILE_NAME_DUPLICATE;
                }

                // ファイルも掲載内容も未入力なら登録処理不可
                if (files.length === 0 && state.information.bodyText.length === 0) {
                    return CommonFunction.replaceArray(
                        MessageConst.E_INPUT_SELECT_AT_LEAST_ONE, ['掲載内容', '添付ファイル']);

                }

                return true;
            }
        ];

        // リンクURLルール
        const urlRules = [
            // 必須チェック
            (v: any) => !!v || CommonFunction.replaceArray(MessageConst.E_REQUIRED, ['URL']),
            // 桁数チェック
            (v: any) => CommonFunction.checkLength(v, maxLength['url'], 'URL'),
            // 属性チェック（http:// か https:// から始まる）
            (v: any) => v === null || v.startsWith(SystemConst.URL_SCHEME['HTTP']) || v.startsWith(SystemConst.URL_SCHEME['HTTPS'])
                || CommonFunction.replaceArray(MessageConst.E_URL_SCHEME_ERR, [SystemConst.URL_SCHEME['HTTP'], SystemConst.URL_SCHEME['HTTPS']])
        ]

        let informationId = '';                                         // 編集対象のお知らせID

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });

        async function init() {
            // お知らせ登録初期表示取得APIで初期表示データを取得する
            // パラメータ：なし
            const response = await ctx.root.$store.dispatch('information/getInitParameters');

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            if (state.isAddMode) {
                // お知らせ登録画面の場合
                // 取得したparameterCodeから掲載開始日・掲載終了日の初期値を設定
                const publishDate = new Date();
                state.information.publishStartDateDisplay = publishDate.getFullYear() + '/' +
                    ('00' + (publishDate.getMonth() + 1)).slice(-2) + '/' +
                    ('00' + publishDate.getDate()).slice(-2);

                // 現在の日付からpublishEndDaysの値分だけ進めた日付を取得
                publishDate.setDate(
                    publishDate.getDate() +
                    Number(response.data.parameters.publishEndDays.value)
                );
                state.information.publishEndDateDisplay = publishDate.getFullYear() + '/' +
                    ('00' + (publishDate.getMonth() + 1)).slice(-2) + '/' +
                    ('00' + publishDate.getDate()).slice(-2);


                // 初期表示した日付を内部データに設定する
                state.information.publishStartDate = state.information.publishStartDateDisplay.replaceAll('/', '-');
                state.information.publishEndDate = state.information.publishEndDateDisplay.replaceAll('/', '-');
            } else {
                // お知らせ編集画面の場合
                // お知らせ更新/削除APIで初期表示データを取得する
                // パラメータ：お知らせID
                const response = await ctx.root.$store.dispatch('information/getOperateInformation',
                    {
                        informationId: informationId
                    }
                );

                // HTTPステータスコードが200以外の場合
                if (response.status !== HttpStatus.OK) {
                    // HTTPステータスコードが200以外の場合
                    if (response.status === HttpStatus.NOT_FOUND) {
                        // HTTPステータスコードが404の場合
                        // displayErrorMessageTransitionDialogメソッドでエラーダイアログを表示する
                        // お知らせ一覧画面へ戻る
                        commonRef.value.displayErrorMessageTransitionDialog(response, '/information/list');
                    } else {
                        // HTTPステータスコードが200, 404以外の場合
                        //errorDisplayTransitionControlメソッドでエラー画面を表示する
                        commonRef.value.errorDisplayTransitionControl(response);
                    }
                    return;
                }

                // 画面項目に初期表示データを設定する
                // タイトル
                state.information.title = response.data.information.title;
                // 掲載開始日
                state.information.publishStartDate
                    = response.data.information.publishStartDate;
                state.information.publishStartDateDisplay
                    = response.data.information.publishStartDate.replaceAll('-', '/');

                // 掲載終了日はDBで必須ではないのでnullチェックを行う
                if (response.data.information.publishEndDate) {
                    state.information.publishEndDate
                        = response.data.information.publishEndDate;
                    state.information.publishEndDateDisplay
                        = response.data.information.publishEndDate.replaceAll('-', '/');
                }

                // 重要度
                state.information.severity = response.data.information.severityCode;
                // 公開範囲
                state.information.publishRange = response.data.information.publicationRangeCode;

                // 削除画面との共通処理にて公開範囲が「指定した組織の全教職員アカウント（一般を含む）」の場合は、
                // 組織の組織型も返ってくる仕様なので、余計な情報を代入しないようにする
                if (state.information.publishRange === publishRange['selectOrganizationType']) {
                    // 組織の種類
                    state.information.organizationType
                        = response.data.information.publicationOrganizationTypeId;
                } else if (state.information.publishRange === publishRange['selectOrganization']) {
                    // 組織
                    state.information.organization = response.data.information.publicationOrganizationId;
                }

                // 発信者
                state.information.sender = response.data.information.sender;
                // 作成方式
                state.information.createMethod = response.data.information.createMethodCode;

                if (state.information.createMethod === createStyleSet['simple']) {
                    // 作成方式が「簡易登録」の場合
                    // 掲載内容を設定する
                    // リッチテキストの未入力時は空文字扱いなので、nullの場合は空文字を設定する
                    if (!response.data.information.body) {
                        state.information.bodyText = "";
                    } else {
                        state.information.bodyText = response.data.information.bodyText;
                    }
                    state.information.body = response.data.information.body;
                    state.information.files = response.data.information.files;
                    // 添付ファイルをfile-pondへ表示する
                    // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                    response.data.information.files.forEach((e: { fileName: string; fileSize: number; fileSequence: number }) => {
                        pondRef.value.addFile(e.fileName, {
                            type: 'local',              // 'local':サーバーにあるファイル 'input':ユーザが追加したファイル
                            file: {
                                name: e.fileName,       // ファイル名
                                size: e.fileSize        // ファイルサイズ
                            },
                            metadata: {
                                url: `/api/information/${informationId}/${e.fileSequence}`   // ダウンロード時のURL
                            }
                        });
                    });

                    // バックアップ用変数に設定
                    backupBody = state.information.body;
                    backupBodyText = state.information.bodyText;
                    backupFiles = JSON.parse(JSON.stringify(state.information.files)); // 配列のディープコピー
                } else {
                    // 作成方式が「リンクURL登録」の場合
                    // リンクURLを設定する
                    state.information.url = response.data.information.url;

                    // バックアップ用変数に設定
                    backupUrl = state.information.url;
                }
            }

            // 以下は登録・編集共通処理
            // ログインユーザーがシステム管理者の場合
            // 「重要度」ラジオボタンに「管理者」項目を表示する
            if (ctx.root.$store.state.authenticationUser.accountDivision === SystemConst.ACCOUNT_DIVISION.ADMIN) {
                state.isAdmin = true;
            }

            // バリデーション値を設定する
            state.validateValues = response.data.parameters;

            // organizationTypesPulldownから組織型名称を取得
            // 公開先（組織の種類）プルダウンにリストを設定
            state.organizationTypesPulldown =
                response.data.organizationTypesPulldown;

            // organizationsPulldownから組織型名称と組織名称を取得
            // 「【組織型名称】組織名称」の文字列を作成して表示用文字列のリストに格納
            // 公開先（組織）プルダウンにリストを設定
            const tmpOrganizations = response.data.organizationsPulldown.organizations;

            for (let index = 0; index < tmpOrganizations.length; index++) {
                tmpOrganizations[index].text =
                    '【' + response.data.organizationsPulldown.organizationTypes[index].text +
                    '】' + tmpOrganizations[index].text;
            }

            state.organizationPulldown = tmpOrganizations;
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // 編集対象のお知らせID取得
            informationId = ctx.root.$route.params.id;

            // 編集対象のお知らせIDが格納されている場合
            if (informationId) {
                // isAddModeをfalseに変更する
                state.isAddMode = false;
            }

            if (state.isAddMode) {
                // state.isAddModeがtrueの場合
                // titleIcon変更
                ctx.root.$store.commit('setTitleIcon', 'mdi-square-edit-outline');
                // mainTitleを「お知らせ登録」に変更
                ctx.root.$store.commit('setMainTitle', 'お知らせ登録');
            } else {
                // state.isAddModeがfalseの場合
                // titleIcon変更（mdi-text-box-check-outline）
                ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-check-outline');
                // mainTitleを「お知らせ編集」に変更
                ctx.root.$store.commit('setMainTitle', 'お知らせ編集');
            }
        }

        /**
         * 日付を表示用にフォーマット変換する
         * 
         * @param isStart true:掲載開始日、false:掲載終了日
         * @param dateValue YYYY-MM-DD形式文字列
         */
        function inputDate(isStart: boolean, dateValue: string) {
            // YYYY-MM-DD ⇒ YYYY/MM/DD
            if (isStart) {
                state.information.publishStartDateDisplay = dateValue.replaceAll('-', '/');
                state.isVisibleStartDateMenu = false;
            } else {
                state.information.publishEndDateDisplay = dateValue.replaceAll('-', '/');
                state.isVisibleEndDateMenu = false;
            }
            // 掲載開始日・掲載終了日の日付チェックのバリデーションチェックを同時に走らせる
            dateRef.value.validate();
        }

        /**
         * テキストボックスに表示されている日付をクリア
         * @param isStart true:掲載開始日、false:掲載終了日
         */
        function clearDate(isStart: boolean) {
            if (isStart) {
                // 表示をクリア(YYYY/MM/DD形式)
                state.information.publishStartDateDisplay = '';
                // 内部保持値をクリア
                state.information.publishStartDate = '';
            } else {
                // 表示をクリア(YYYY/MM/DD形式)
                state.information.publishEndDateDisplay = '';
                // 内部保持値をクリア
                state.information.publishEndDate = '';
            }
        }

        /**
         * 公開先選択で選択したデータを、公開先確認ダイアログで表示する配列に格納
         */
        function selectPulldown() {
            let j = 0;

            if (state.information.publishRange === publishRange['selectOrganizationType']) {
                // 公開範囲が「指定した組織種類の全教職員アカウント（一般を含む）」の場合
                state.organizationTypesList = [];

                for (let i = 0; i < state.organizationTypesPulldown.length; i++) {
                    if (state.information.organizationType.indexOf(state.organizationTypesPulldown[i].value) !== -1) {
                        state.organizationTypesList[j++] = state.organizationTypesPulldown[i].text;
                    }
                }
            } else if (state.information.publishRange === publishRange['selectOrganization']) {
                // 公開範囲が「指定した組織の全教職員アカウント（一般を含む）」の場合
                state.organizationList = [];

                for (let i = 0; i < state.organizationPulldown.length; i++) {
                    if (state.information.organization.indexOf(state.organizationPulldown[i].value) !== -1) {
                        state.organizationList[j++] = state.organizationPulldown[i].text;
                    }
                }

            }
        }

        /**
         * 登録確認ダイアログ表示
         */
        function displayDialog() {
            // バリデーションを行う
            //   ・タイトル、発信者フォームのバリデーション
            // 作成形式が「リンクURL登録」の場合
            //   ・URLのバリデーション
            const isForm = form.value.validate();
            // 掲載期間のバリデーション
            // 掲載期間の逆転チェック
            const isDate = dateRef.value.validate();
            // 作成形式が「簡易登録」の場合
            //   ・掲載内容（リッチテキスト）のバリデーション
            //   ・添付ファイルのバリデーション
            if (state.information.createMethod === createStyleSet['simple']) {
                validateBodyFiles();
            } else {
                // 作成方式が簡易登録でない場合は、バリデーションチェックフラグをtrueにしておく
                state.isBodyValid = true;
                state.isFileValid = true;
            }

            if (!isForm || !isDate || !state.isBodyValid || !state.isFileValid) {
                // バリデーションエラーの際にフォームの一番上にスクロールする
                const formScroll = document.getElementById('formScroll');
                if (formScroll !== null) {
                    formScroll.scrollTo(0, 0);
                }
                return;
            }

            let displayMessage = '';
            // 登録処理・編集処理の判定
            if (state.isAddMode) {
                // 登録処理の場合
                // 登録処理実行ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['登録']);
            } else {
                // 編集処理の場合
                // 編集処理実行確認ダイアログに表示するメッセージを格納する
                displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['編集']);
            }

            // 確認メッセージダイアログ表示
            //   「はい」をクリックした場合はprocessingData()メソッドが呼び出され、
            //   「いいえ」をクリックした場合はダイアログを閉じ、お知らせ登録/編集画面へ戻る
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * リッチテキストエディタの入力イベント
         * 
         * @param body 掲載内容(HTML形式)
         * @param bodyText 掲載内容(文字のみ)
         */
        function textChange(body: string, bodyText: string) {
            // 値が変わった時だけstateを更新
            if (state.information.body !== body || state.information.bodyText !== bodyText) {
                state.information.body = body;
                state.information.bodyText = bodyText;
                validateBodyFiles();
            }
        }

        /**
         * 添付ファイルへファイルを追加・削除された時のイベント
         */
        function updateFiles() {
            validateBodyFiles();
        }

        /**
         * 掲載内容と添付ファイルのバリデーションチェック処理
         * いずれかが入力必須であるため、同時にチェックする
         */
        function validateBodyFiles() {

            // 掲載内容のバリデーションを行う
            for (let index = 0; index < bodyRules.length; index++) {
                const rule: any = bodyRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.bodyErrorMessage = valid;
                    state.isBodyValid = false;
                    break;
                } else {
                    // エラーなし
                    state.bodyErrorMessage = '';
                    state.isBodyValid = true;
                }
            }

            // 添付ファイルのバリデーションを行う
            for (let index = 0; index < filesRules.length; index++) {
                const rule: any = filesRules[index];
                const valid = typeof rule === 'function' ? rule() : rule
                if (valid !== true) {
                    // エラーあり
                    state.fileErrorMessage = valid;
                    state.isFileValid = false;
                    break;
                } else {
                    // エラーなし
                    state.fileErrorMessage = '';
                    state.isFileValid = true;
                }
            }

        }

        /**
         * リンクURL登録選択時、掲載内容・添付ファイルのクリア
         */
        function clearReferenceUrl() {
            // 編集モードの場合
            if (!state.isAddMode) {
                // リンクURL復元
                state.information.url = backupUrl;
            }

            // 変数初期化
            // 掲載内容
            state.information.body = '';
            state.information.bodyText = '';
            // 添付ファイル（nextTickでDOM更新後に実行）
            nextTick(() => {
                pondRef.value.removeFiles();
            });

            // バリデーション初期化
            // 掲載内容
            state.bodyErrorMessage = '';
            state.isBodyValid = true;
            // 添付ファイル
            state.fileErrorMessage = '';
            state.isFileValid = true;
        }

        /**
         * 簡易登録選択時、リンクURLのクリア
         */
        function clearLinkUrl() {
            // 編集モードの場合
            if (!state.isAddMode) {
                // リッチテキストエディタの再描画（keyを変更することで再描画）
                state.bodyEditorKey = Date.now();

                // 掲載内容・添付ファイル復元
                nextTick(() => {
                    state.information.body = backupBody;
                    state.information.bodyText = backupBodyText;
                });
                state.information.files = JSON.parse(JSON.stringify(backupFiles)); // 配列のディープコピー

                // FilePondのファイルを再設定（nextTickでDOM更新後に実行）
                nextTick(() => {
                    pondRef.value.removeFiles();
                    state.information.files.forEach((e: any) => {
                        pondRef.value.addFile(e.fileName, {
                            type: 'local',
                            file: {
                                name: e.fileName,
                                size: e.fileSize
                            },
                            metadata: {
                                url: `/api/information/${informationId}/${e.fileSequence}`
                            }
                        });
                    });
                });
            }

            // 変数初期化
            // リンクURL
            state.information.url = '';
        }

        /**
         * 確認メッセージダイアログの「はい」押下時の処理
         */
        async function processingData() {
            const information = {
                'title': state.information.title,
                'publishStartDate': state.information.publishStartDate,
                'publishEndDate': state.information.publishEndDate,
                'severityCode': state.information.severity,
                'publicationRangeCode': state.information.publishRange,
                'publicationOrganizationTypeId': [''],
                'publicationOrganizationId': [''],
                'sender': state.information.sender,
                'createMethodCode': state.information.createMethod,
                'body': '',
                'bodyText': '',
                'url': ''
            };
            let files = [];
            let response: any;

            if (state.information.publishRange === publishRange['selectOrganizationType']) {
                // 公開範囲が「指定した組織種類の全教職員アカウント（一般を含む）」の場合
                // ・引数に公開先の組織型を設定
                information['publicationOrganizationTypeId'] = state.information.organizationType;
                information['publicationOrganizationId'] = [];

            } else if (state.information.publishRange === publishRange['selectOrganization']) {
                // 公開範囲が「指定した組織の全教職員アカウント（一般を含む）」の場合
                // ・引数に公開先の組織IDを設定
                information['publicationOrganizationId'] = state.information.organization;
                information['publicationOrganizationTypeId'] = [];
            } else {
                information['publicationOrganizationTypeId'] = [];
                information['publicationOrganizationId'] = [];
            }

            if (state.information.createMethod === createStyleSet['simple']) {
                // 作成方式が簡易登録の場合
                // ・引数の掲載内容を設定
                // ・引数の添付ファイルをFilePondから取得
                information['body'] = state.information.body;
                information['bodyText'] = state.information.bodyText;
                files = pondRef.value.getFiles().map((e: FilePondFile) => e.file);
            } else if (state.information.createMethod === createStyleSet['link']) {
                // 作成方式がリンクURL登録の場合
                // ・引数のURLを設定
                information['url'] = state.information.url;
            }

            // 登録処理・編集処理の判断
            // お知らせ登録画面の場合
            if (state.isAddMode) {
                // お知らせ登録APIでデータを登録する
                // POST /api/information
                response = await ctx.root.$store.dispatch(
                    'information/addInformation',
                    {
                        information,
                        files: files
                    }
                );
            } else {
                // お知らせ編集画面の場合
                let realFiles: File[] = [];

                // 作成方式が簡易登録の場合
                if (state.information.createMethod === createStyleSet['simple']) {
                    const files: FilePondFile[] = pondRef.value.getFiles();
                    // information.files（DB上のファイル）が、files（アップロードするファイル）
                    // にないものはinformation.filesから消す
                    const newFiles: InformationFile[] = [];
                    state.information.files.forEach(e => {
                        if (files.filter(x => x.file.name === e.fileName).length !== 0) {
                            newFiles.push(e);
                        }
                    });
                    state.information.files = newFiles;

                    // ただし、同じファイル名の場合は、更新された（入れ替えた）ファイルと判断し、
                    // ファイルサイズのみコピーし、ファイルシーケンスを削除する。
                    files.forEach(e => {
                        if (e.origin === FileOrigin.INPUT) {   // 'INPUT' = ユーザ入力のファイル
                            if (state.information.files.filter(x => x.fileName === e.file.name).length !== 0) {
                                const r = state.information.files.find(x => x.fileName === e.file.name);
                                if (r) {
                                    // ファイルサイズを更新・ファイルシーケンスを削除
                                    r.fileSize = e.fileSize;
                                    r.fileSequence = undefined;
                                }
                            } else {
                                // DBに無い、新しいファイル
                                state.information.files.push({
                                    'fileName': e.file.name,
                                    'fileSize': e.fileSize
                                });
                            }
                        }
                    });

                    // files（アップロードするファイル）をinformation.filesへ追加する。
                    realFiles = pondRef.value.getFiles().map((e: FilePondFile) => e.file);
                } else {
                    // 作成方式がリンクURL登録の場合
                    // 設定済のファイル情報をリセットする
                    state.information.files = [];
                }

                // お知らせ編集APIでデータを編集する
                // POST /api/information/{informationId}
                response = await ctx.root.$store.dispatch(
                    'information/updateInformation',
                    {
                        informationId: informationId,
                        information:
                        {
                            'title': information.title,
                            'publishStartDate': information.publishStartDate,
                            'publishEndDate': information.publishEndDate,
                            'severityCode': information.severityCode,
                            'publicationRangeCode': information.publicationRangeCode,
                            'publicationOrganizationTypeId': information.publicationOrganizationTypeId,
                            'publicationOrganizationId': information.publicationOrganizationId,
                            'sender': information.sender,
                            'createMethodCode': information.createMethodCode,
                            'body': information.body,
                            'bodyText': information.bodyText,
                            'url': information.url,
                            'files': state.information.files
                        },
                        files: realFiles
                    }
                );
            }

            // 以下は登録・編集共通処理
            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    //  HTTPステータスコードが404の場合
                    //  displayErrorMessageTransitionDialogメソッドでお知らせ一覧画面に遷移
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/information/list');
                } else {
                    //  HTTPステータスコードが404以外の場合
                    //  errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            // 登録完了メッセージダイアログ表示
            commonRef.value.displayMessageTransitionDialog(
                response,
                '/information/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            // storeのページ番号保持フラグをtrueに設定する
            ctx.root.$store.state.information.isSavePage = true;

            // お知らせ一覧に遷移する
            router.back();
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };

            // ウィンドウの幅が狭まってリッチテキストの縦幅が広がった場合はバリデーションメッセージ表示位置を下げる
            if (state.windowSize.x > 1265) {
                state.isToolbarBr = false;
            } else {
                state.isToolbarBr = true;
            }
        }

        return {
            form,
            dateRef,
            pondRef,
            severity,
            commonRef,
            createStyleSet,
            publishRange,
            state: readonly(state),
            maxLength,
            titleRules,
            publishDateRules,
            publishRangeRules,
            senderRules,
            bodyRules,
            filesRules,
            urlRules,
            formHeight,
            inputDate,
            clearDate,
            selectPulldown,
            displayDialog,
            textChange,
            updateFiles,
            clearReferenceUrl,
            clearLinkUrl,
            processingData,
            back,
            onResize
        };
    }
});
